import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { ApiResponse } from "@/api/types";
import { EMPTY_ACCESS, NO_VERBS, type EffectiveAccess, type ObjectVerbs } from "./types";

interface AccessContextValue {
  access: EffectiveAccess;
  /** False until /access/me has answered. Deny-by-default while loading. */
  isReady: boolean;
  /** May the user see this object at all? */
  can: (slug: string) => boolean;
  /** Full verb set for an object; all-false when not granted. */
  verbs: (slug: string) => ObjectVerbs;
  /** Does the user hold this endpoint permission, at any scope? */
  hasPermission: (name: string) => boolean;
  /** Does the user hold this role code? */
  hasRole: (code: string) => boolean;
}

const AccessContext = createContext<AccessContextValue | null>(null);

/**
 * Fetches the signed-in user's effective access once and shares it.
 *
 * Access is resolved server-side from role membership, so it is not derivable
 * from anything the client already holds — the JWT deliberately does not carry
 * it, because a revoked grant would then stay live until the token expired.
 *
 * Mount this INSIDE the auth provider: it only queries when a user exists, and
 * the query key includes the user id so switching accounts (including exiting
 * impersonation) refetches rather than serving the previous user's access.
 */
export function AccessProvider({
  userId,
  children,
}: {
  userId?: string | null;
  children: ReactNode;
}) {
  const { data, isSuccess } = useQuery({
    queryKey: ["access", userId],
    enabled: !!userId,
    // Access changes only when an admin edits a grant, so it need not be
    // chatty — but it must not be infinite either, or a revocation would never
    // reach an open tab.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<EffectiveAccess>>("/access/me");
      return data.data;
    },
  });

  const value = useMemo<AccessContextValue>(() => {
    const access = data ?? EMPTY_ACCESS;
    const visible = new Set(access.visible ?? []);
    return {
      access,
      isReady: isSuccess,
      can: (slug) => visible.has(slug),
      verbs: (slug) => access.objects?.[slug] ?? NO_VERBS,
      hasPermission: (name) => name in (access.permissions ?? {}),
      hasRole: (code) => (access.roles ?? []).includes(code),
    };
  }, [data, isSuccess]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessContextValue {
  const ctx = useContext(AccessContext);
  if (!ctx) {
    throw new Error("useAccess must be used within an AccessProvider");
  }
  return ctx;
}
